import { UUID } from "./models/model";

export type IDOwner<ID extends {} = UUID> = { id: ID } | ID;
function ID<ID extends {}>(id: IDOwner<ID>) {
    if (typeof id === "object" && "id" in id) return id.id;
    else return id;
}

export class Collection<T extends { id: ID }, ID extends {} = UUID> implements Iterable<T> {
    private _objects = new Map<ID, T>();

    public get size() {
        return this._objects.size;
    }

    public tryGet(id: IDOwner<ID>): T | undefined {
        return this._objects.get(ID(id)) as T;
    }
    public get(id?: undefined): undefined;
    public get(id: IDOwner<ID>): T;
    public get(id?: IDOwner<ID>): T | undefined;
    public get(id?: IDOwner<ID>) {
        if (id == null) return undefined;
        const res = this.tryGet(id);

        if (res == null) throw new Error(`Object ${id} doesn't exist`);
        else return res;
    }

    public tryGetAll(ids: Iterable<IDOwner<ID> | undefined | null>): T[] | undefined {
        const res = new Set<T>();

        for (const id of ids) {
            if (id == null) continue;

            const el = this.tryGet(id);
            if (el == null) return undefined;

            res.add(el);
        }

        return [...res];
    }
    public getAll(ids: Iterable<IDOwner<ID> | null | undefined>): T[] {
        const res = new Set<T>();

        for (const id of ids) {
            if (id == null) continue;
            res.add(this.get(id));
        }

        return [...res];
    }
    public getSome(ids: Iterable<IDOwner<ID> | undefined>): T[] {
        const res = new Set<T>();

        for (const id of ids) {
            if (id == null) continue;

            const el = this.tryGet(id);
            if (el != null) res.add(el);
        }

        return [...res];
    }

    public getAllBy(
        keys: object[],
        thatKey?: string,
        thisKey?: string
    ): T[] {
        const ids = new Set<ID>(keys
            .filter((v): v is {} => v != null)
            .map(v => {
                if (thatKey != null && typeof v === "object" && thatKey in v) return (v as any)[thatKey];
                else return v;
            })
        );

        if (thisKey == null || thisKey === "id") return this.getAll([...ids]);

        const res = new Set<T>();
        for (const obj of this) {
            if (ids.has((obj as any)[thisKey])) res.add(obj);
        }

        return [...res];
    }

    public first() {
        return this._objects.values().next().value;
    }
    public all() {
        return this._objects.values();
    }
    public array() {
        return [...this.all()];
    }
    public map() {
        return this._objects;
    }

    public [Symbol.iterator]() {
        return this.all();
    }

    public constructor(objs?: Iterable<T>) {
        for (const obj of objs ?? []) {
            this._objects.set(obj.id, obj);
        }
    }

    public static empty<T extends { id: ID }, ID extends {} = string>() {
        return new Collection<T, ID>();
    }
}
export namespace Dates {
    export function correct<T>(date: T): T {
        if (date instanceof Date) {
            return new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000) as T;
        }
        else {
            return date;
        }
    }
    export function date(date: Date) {
        return date.toISOString().split('T')[0];
    }
}

/**
 * A hybrid structure between Map and Set (a map of sets)
 */
export class MultiMap<K, V> {
    private _groupToVal = new Map<K, Map<any, V>>();
    private _valToGroup = new Map<any, K>();

    public get size() { return this._groupToVal.size; }

    public clear() {
        this._groupToVal.clear();
        this._valToGroup.clear();
    }
    public delete(group: K, val?: V) {
        if (arguments.length > 1) {
            const key = this.keyFunc(val!);
            this._valToGroup.delete(key);

            const map = this._groupToVal.get(group);
            if (map == null) return false;
            if (!map.delete(key)) return false;

            if (map.size == 0) this._groupToVal.delete(group);
            return true;
        }
        else {
            return this._groupToVal.delete(group);
        }
    }

    public get(key: K): Set<V> {
        return new MultiMapSet(this, key);
    }

    public getRawMap(key: K) {
        return this._groupToVal.get(key);
    }

    public set(groupKey: K, val: V) {
        let group = this._groupToVal.get(groupKey);
        if (group == null) {
            group = new Map();
            this._groupToVal.set(groupKey, group);
        }

        const prevSize = group.size;
        const key = this.keyFunc(val);

        const oldGroup = this._valToGroup.get(key);
        if (oldGroup != null) this._groupToVal.get(oldGroup)!.delete(key);

        group.set(this.keyFunc(val), val);

        this._valToGroup.set(key, groupKey);

        return group.size > prevSize;
    }

    public addAll(key: K, val: Iterable<V>) {
        let n = 0;

        for (const el of val) {
            n += this.set(key, el) ? 1 : 0;
        }

        return n;
    }

    public has(key: K, val: V) {
        return this._groupToVal.get(key)?.has(this.keyFunc(val)) ?? false;
    }
    public getGroup(val: V) {
        return this._valToGroup.get(val);
    }

    public forEach(callbackfn: (val: V, key: K, self: MultiMap<K, V>) => void, thisArg?: any) {
        for (const [k, v] of this) {
            callbackfn.call(thisArg, v, k, this);
        }
    }

    public *[Symbol.iterator](): IterableIterator<[K, V]> {
        for (const [k, v] of this._groupToVal) {
            for (const [_, el] of v) {
                yield [k, el];
            }
        }
    }

    public entries(): IterableIterator<[K, V]> {
        return this[Symbol.iterator]();
    }
    public *keys(): IterableIterator<K> {
        for (const [k] of this) yield k;
    }
    public *values(): IterableIterator<V> {
        for (const [_, v] of this) yield v;
    }

    public constructor(
        public readonly keyFunc: (val: V) => any = v => v
    ) { }
}

export class MultiMapSet<K, V> implements Set<V> {
    public get size() {
        return this.map.getRawMap(this.key)?.size ?? 0;
    }

    public add(value: V): this {
        this.map.set(this.key, value);
        return this;
    }
    public clear(): void {
        this.map.delete(this.key);
    }
    public delete(value: V): boolean {
        return this.map.delete(this.key, value);
    }
    public forEach(callbackfn: (a: V, b: V, set: MultiMapSet<K, V>) => void, thisArg?: any): void {
        for (const [_, v] of this.map.getRawMap(this.key) ?? []) {
            callbackfn.call(thisArg, v, v, this);
        }
    }
    public has(value: V): boolean {
        return this.map.has(this.key, value);
    }

    public *entries(): IterableIterator<[V, V]> {
        for (const el of this) yield [el, el];
    }
    public *keys(): IterableIterator<V> {
        for (const el of this) yield el;
    }
    public *values(): IterableIterator<V> {
        for (const el of this) yield el;
    }
    public *union<K, V>(other: any): any{
        throw new Error('Not implemented');
    }
    public *intersection<K, V>(other: any): any{
        throw new Error('Not implemented');
    }
    public *difference<K, V>(other: any): any{
        throw new Error('Not implemented');
    }
    public *symmetricDifference<K, V>(other: any): any{
        throw new Error('Not implemented');
    }
    public *isSubsetOf<K, V>(other: any): any{
        throw new Error('Not implemented');
    }
    public *isSupersetOf<K, V>(other: any): any{
        throw new Error('Not implemented');
    }
    public *isDisjointFrom<K, V>(other: any): any{
        throw new Error('Not implemented');
    }

    public *[Symbol.iterator](): IterableIterator<V> {
        for (const [_, el] of this.map.getRawMap(this.key) ?? []) {
            yield el;
        }
    }
    public [Symbol.toStringTag]: string = "MultiMapSet";

    public constructor(
        public readonly map: MultiMap<K, V>,
        public readonly key: K,
    ) { }
}
